import type { ChecklistStatus } from '../../shared/checklistUtils';
import { getStatusLabel, isFiniteNumber } from '../../shared/checklistUtils';

import type { BlackoutChecklistRow, BlackoutCoverageSummary } from './types';

const BLACKOUT_DATES_FIELD = 'Blackout Dates';
export const FESTIVE_DATE_LABEL = 'Dec 24, Dec 25, Dec 31';

const DAY_MS = 24 * 60 * 60 * 1000;

type BlackoutDateInput = App.OpportunityPage['black_out_dates'][number] & {
  sfid?: string;
};

type DateRange = {
  end: number;
  start: number;
};

export function buildBlackoutRows({
  blackOutDates,
  hasSourceError,
  travelFrom,
  travelTo,
}: {
  blackOutDates?: Array<BlackoutDateInput>;
  hasSourceError?: boolean;
  travelFrom?: string;
  travelTo?: string;
}): Array<BlackoutChecklistRow> {
  if (hasSourceError) {
    return [
      evaluateBlackoutRow({
        id: 'blackout-source-unavailable',
        name: 'Opportunity blackout dates unavailable',
        travelFrom,
        travelTo,
      }),
    ];
  }

  return (blackOutDates ?? []).map((blackoutDate, index) =>
    evaluateBlackoutRow({
      from: blackoutDate.from__c,
      id: getBlackoutRowId(blackoutDate, index),
      name: blackoutDate.name || `Blackout ${index + 1}`,
      to: blackoutDate.to__c,
      travelFrom,
      travelTo,
    }),
  );
}

function getBlackoutRowId(blackoutDate: BlackoutDateInput, index: number) {
  if (blackoutDate.sfid) {
    return blackoutDate.sfid;
  }

  return blackoutDate.opportunity__c
    ? `${blackoutDate.opportunity__c}:${blackoutDate.name || index}:${blackoutDate.from__c || ''}:${
        blackoutDate.to__c || ''
      }`
    : `blackout:${index}`;
}

export function evaluateBlackoutRow({
  from,
  id,
  name,
  to,
  travelFrom,
  travelTo,
}: {
  from?: string;
  id: string;
  name: string;
  to?: string;
  travelFrom?: string;
  travelTo?: string;
}): BlackoutChecklistRow {
  const blackoutRange = parseDateRange(from, to);
  const travelRange = parseDateRange(travelFrom, travelTo);
  const withinTravelValidity =
    !!blackoutRange &&
    !!travelRange &&
    blackoutRange.start >= travelRange.start &&
    blackoutRange.end <= travelRange.end;
  const festiveClear = !!blackoutRange && !rangeOverlapsFestiveDates(blackoutRange);
  const status: ChecklistStatus = withinTravelValidity && festiveClear ? 'approved' : 'requires approval';

  return {
    festiveClear,
    field: BLACKOUT_DATES_FIELD,
    from,
    id,
    name,
    status,
    statusLabel: getStatusLabel(status),
    to,
    withinTravelValidity,
  };
}

export function calculateBlackoutCoverage({
  blackOutDates,
  travelFrom,
  travelTo,
}: {
  blackOutDates: Array<Pick<BlackoutChecklistRow, 'from' | 'to'>>;
  travelFrom?: string;
  travelTo?: string;
}): BlackoutCoverageSummary {
  const travelRange = parseDateRange(travelFrom, travelTo);
  if (!travelRange) {
    return {
      blackoutDays: 0,
      percent: 0,
      status: 'requires approval',
    };
  }

  const ranges = blackOutDates
    .map((blackoutDate) => parseDateRange(blackoutDate.from, blackoutDate.to))
    .filter((range): range is DateRange => !!range)
    .map((range) => ({
      end: Math.min(range.end, travelRange.end),
      start: Math.max(range.start, travelRange.start),
    }))
    .filter((range) => range.start <= range.end)
    .sort((a, b) => a.start - b.start);
  const mergedRanges = mergeDateRanges(ranges);
  const blackoutDays = mergedRanges.reduce((sum, range) => sum + inclusiveDays(range), 0);
  const travelDays = inclusiveDays(travelRange);
  const percent = travelDays > 0 ? Math.round((blackoutDays / travelDays) * 100) : 0;

  return {
    blackoutDays,
    percent,
    status: percent < 75 ? 'approved' : 'requires approval',
    travelDays,
  };
}
function parseDateRange(from?: string, to?: string): DateRange | undefined {
  const start = parseDateToUtcDay(from);
  const end = parseDateToUtcDay(to);

  if (!isFiniteNumber(start) || !isFiniteNumber(end) || end < start) {
    return undefined;
  }

  return { end, start };
}

function parseDateToUtcDay(value?: string) {
  if (!value) {
    return undefined;
  }

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

function rangeOverlapsFestiveDates(range: DateRange) {
  const startYear = new Date(range.start).getUTCFullYear();
  const endYear = new Date(range.end).getUTCFullYear();

  for (let year = startYear; year <= endYear; year += 1) {
    const festiveDays = [Date.UTC(year, 11, 24), Date.UTC(year, 11, 25), Date.UTC(year, 11, 31)];
    if (festiveDays.some((day) => day >= range.start && day <= range.end)) {
      return true;
    }
  }

  return false;
}

function mergeDateRanges(ranges: Array<DateRange>) {
  return ranges.reduce<Array<DateRange>>((merged, range) => {
    const previousRange = merged[merged.length - 1];

    if (!previousRange || range.start > previousRange.end + DAY_MS) {
      merged.push({ ...range });
      return merged;
    }

    previousRange.end = Math.max(previousRange.end, range.end);
    return merged;
  }, []);
}

function inclusiveDays(range: DateRange) {
  return Math.round((range.end - range.start) / DAY_MS) + 1;
}
